<template>
    <div class="team-lead-day-planning">
        <div
            v-for="date in dates"
            :key="date.formatted"
            class="team-lead-day-planning__day"
        >
            <div class="team-lead-day-planning__day-title">
                <div class="team-lead-day__day">
                    {{ date.formatted }}
                </div>
                <div class="team-lead-blocks">
                    <div 
                        v-for="teamLead in getOrderedTeamLeadsForDate(date.formatted)" 
                        :key="teamLead.id"
                        class="team-lead-block"
                    >
                        <div class="team-lead-name">
                            {{ teamLead.name }}
                        </div>
                        <div class="team-lead-slots">
                            <div 
                                v-for="slot in teamLead.slots" 
                                :key="slot.id"
                                class="team-lead-slot"
                                @click="openPlanningForm(slot)"
                                :style="{...slotStyle(slot), ...getSlotBorderStyle(slot)}"
                                @dragover.prevent
                                @drop="handleSlotDrop($event, slot)"
                            >
                                <div class="team-lead-slot-info">
                                    <div class="team-lead-slot-title">
                                        {{ slot.project_title ? slot.project_title+' - ' : '' }} {{ slot.task_title }} 
                                    </div>
                                    <div class="team-lead-slot-persons">
                                        <div 
                                            v-for="personId in getOrderedPersonIds(slot.person_ids)"
                                            :key="slot.id + '-' + personId"
                                            class="team-lead-slot-person team-lead-slot-asset"
                                            :style="{...getAssetStyle('person', personId), ...getBorderColorForDateAndPerson(slot.date, getPerson(personId))}"
                                            draggable="true"
                                            @dragstart="handleDragStartPerson($event, slot, getPerson(personId))"
                                            @dragend="handleDragEnd"
                                            @mouseenter="!isDragging && showPersonTooltip($event, personId, slot.date)"
                                            @mouseleave="!isDragging && hidePersonTooltip($event, personId)"
                                        >
                                            <span
                                                class="status-dot"
                                                :class="getPerson(personId)?.latest_badge_time?.type === 'in' ? 'green' : 'red'"
                                            ></span>
                                            {{ getPerson(personId)?.name }}
                                        </div>
                                        <div 
                                            v-for="equipmentId in slot.equipment_ids"
                                            :key="slot.id + '-' + equipmentId"
                                            class="team-lead-slot-equipment team-lead-slot-asset"
                                            :style="{...getAssetStyle('equipment', equipmentId), ...getBorderColorForSlotAndEquipment(slot, equipmentId)}"
                                            draggable="true"
                                            @dragstart="handleDragStartEquipment($event, slot, getEquipment(equipmentId))"
                                            @dragend="handleDragEnd"
                                        >
                                            {{ getEquipment(equipmentId)?.title }}
                                        </div>
                                    </div>
                                </div>
                                <div class="team-lead-slot-timings">
                                    <div class="team-lead-slot-timing">
                                        <div class="team-lead-slot-timing-duration text-right">
                                            {{ slot.start_time }} - {{ slot.end_time }} | {{ slot.foramtted_duration }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <PersonTooltipComponent
        ref="personTooltip"
        :fetch-delay="hoverDelay"
    />
</template>


<script>
import {mapState} from "vuex";
import axios from "axios";
import {emitter} from "../../../plugins/Mitt";
import PersonTooltipComponent from "../../person/PersonTooltipComponent.vue";
import { buildPlanningFilter } from "../../../utils/planning/buildPlanningFilter";

export default {
    components: {
        PersonTooltipComponent
    },
    data(){
        return {
            teamLeadSlots: {
                slots: {},
                person_slots: {},
                persons: {},
                equipments: {},
                equipment_conflicts: {}
            },
            isDragging: false
        }
    },
    computed: {
        ...mapState('planning', ['dateStart', 'dateEnd', 'dates', 'defaultWeekschedule', 'hoverDelay']),
        ...mapState('planningFilter', ['filter','filterLoaded']),
    },
    methods:{
        showPersonTooltip(event, personId, date) {
            this.$refs.personTooltip.activateTooltipForPerson(event, personId, date);
        },
        hidePersonTooltip(event, personId) {
            this.$refs.personTooltip.deactivateOrCancelTooltip(event, personId);
        },
        getPerson(personId) {
            return this.teamLeadSlots.persons[personId] || null;
        },
        getEquipment(equipmentId) {
            return this.teamLeadSlots.equipments[equipmentId] || null;
        },
        getAssetStyle(type, id) {
            const asset = type === 'person' ? this.getPerson(id) : this.getEquipment(id);
            if (!asset) {
                return {
                    background: '#ccc',
                    color: '#000',
                };
            }
            return {
                background: asset.status_color || '#ccc',
                color: asset.status_text_color || '#000',
            };
        },
        getSlots() {
            if( !this.filterLoaded || !this.dateStart || !this.dateEnd){
                return;
            }
            const start = this.dateStart;
            const end = this.dateEnd;

            const url = route('planning.team-lead-planning.data');

            const allowedFilters = ['department_ids', 'team_ids'];
            const filter = buildPlanningFilter(this.filter, allowedFilters, { dateStart: start, dateEnd: end });

            const params = {
                date_start: start,
                date_end: end,
                filter: filter,
            };

            axios.get(url, {params})
                .then(response => {
                    this.teamLeadSlots = response.data;
                })
                .catch(error => {
                    console.error('Error fetching slots:', error);
                });
        },
        getTeamLeadSlotsForDate(date) {
            const personSlots = this.teamLeadSlots.person_slots[date] || {};
            const result = {};
            
            // First, process all person slots
            Object.entries(personSlots).forEach(([teamLeadId, slotIds]) => {
                result[teamLeadId] = slotIds.map(slotId => this.teamLeadSlots.slots[slotId]).filter(slot => slot);
            });
            
            // Get all team lead IDs and sort them: 0 first, then by person name
            const allTeamLeadIds = Object.keys(result);
            
            // Sort: put '0' first, then sort others by name
            const sortedTeamLeadIds = allTeamLeadIds.sort((a, b) => {
                // Always put '0' first
                if (a == '0' || a == 0) return -1;
                if (b == '0' || b == 0) return 1;
                
                // Sort others by person name
                const personA = this.getPerson(parseInt(a));
                const personB = this.getPerson(parseInt(b));
                
                // Handle cases where person might not be found
                if (!personA && !personB) return 0;
                if (!personA) return 1;
                if (!personB) return -1;
                
                return personA.name.localeCompare(personB.name);
            });
            
            // Build the sorted result object
            const sortedResult = {};
            sortedTeamLeadIds.forEach(teamLeadId => {
                sortedResult[teamLeadId] = result[teamLeadId];
            });
            
            return sortedResult;
        },
        getTeamLeadName(teamLeadId) {
            if (teamLeadId == 0) {
                return trans('None');
            }
            const person = this.getPerson(teamLeadId);
            return person ? person.name : '';
        },
        slotStyle(slot) {
            return {
                background: slot.task_status_color ? `${slot.task_status_color}80` : '#fff',
                color: slot.task_status_text_color || '#000',
            }
        },
        openPlanningForm(slot){
            emitter.emit('open-planning-form', {
                slotId: slot.id,
            });
        },
        handleDropzoneDrop($event, date){
            let personId = $event.dataTransfer.getData('personId');

            const defaultWeekschedule = this.defaultWeekschedule;
            let day = defaultWeekschedule[date.formatted];

            let eventObject = {
                dateStart: date.formatted,
                dateEnd: date.formatted,
                timeStart: day.time_start,
                timeEnd: day.time_end,
            };
            if( personId ){
                eventObject.personId = personId;
            }

            emitter.emit('open-planning-form', eventObject);
        },
        handleDragEnd($event) {
            const body = document.body;
            body.classList.remove('cursor-grabbing');
            this.isDragging = false;
            emitter.emit('drag-end');
        },
        handleDragStartPerson($event, slot, person) {
            if (!person) return;
            
            const body = document.body;
            body.classList.add('cursor-grabbing');
            this.isDragging = true;

            // Hide any active tooltip when dragging starts
            this.hidePersonTooltip($event, person.id);

            $event.dataTransfer.setData('startSlotId', slot.id);
            $event.dataTransfer.setData('personId', person.id);
            $event.dataTransfer.setData('type', 'person');
        },
        handleDragStartEquipment($event, slot, equipment) {
            if (!equipment) return;
            
            const body = document.body;
            body.classList.add('cursor-grabbing');
            this.isDragging = true;

            $event.dataTransfer.setData('startSlotId', slot.id);
            $event.dataTransfer.setData('equipmentId', equipment.id);
            $event.dataTransfer.setData('type', 'equipment');
        },
        handleSlotDrop($event, slot) {
            let personId = Number($event.dataTransfer.getData('personId'));
            let equipmentId = Number($event.dataTransfer.getData('equipmentId'));
            const startSlotId = Number($event.dataTransfer.getData('startSlotId'));

            if (!personId && !equipmentId) {
                return;
            }

            if (slot.id === startSlotId) {
                return;
            }

            let assets = {};

            if (personId) {
                let personIds = [...slot.person_ids];
                personIds.push(personId);
                assets['personIds'] = personIds;
            }

            if (equipmentId) {
                let equipmentIds = [...slot.equipment_ids];
                equipmentIds.push(equipmentId);
                assets['equipmentIds'] = equipmentIds;
            }

            const url = route('planning.sync-resources', {'slotId': slot.id});
            axios.post(url, assets)
                .then((response) => {
                    if (startSlotId) {
                        this.removeAssetsFromSlot(startSlotId, personId, equipmentId);
                        return;
                    }
                    this.getSlots();
                    emitter.emit('planning-drop-handled');
                })
                .catch(error => {
                    console.error('Error syncing resources:', error);
                });
        },
        removeAssetsFromSlot(slotId, personId, equipmentId) {
            // Find the slot in the slots object directly
            const slot = this.teamLeadSlots.slots[slotId];

            if (!slot || (!personId && !equipmentId)) {
                return;
            }

            let assets = {};

            if (personId) {
                let personIds = slot.person_ids.filter(id => id !== personId);
                assets['personIds'] = personIds;
            }

            if (equipmentId) {
                let equipmentIds = slot.equipment_ids.filter(id => id !== equipmentId);
                assets['equipmentIds'] = equipmentIds;
            }

            const url = route('planning.sync-resources', {'slotId': slot.id});
            axios.post(url, assets)
                .then((response) => {
                    this.getSlots();
                    emitter.emit('planning-drop-handled');
                })
                .catch(error => {
                    console.error('Error removing assets:', error);
                });
        },
        findSlotById(slotId) {
            return this.teamLeadSlots.slots[slotId] || null;
        },
        getTeamLeadAbsencesForDate(date, teamLeadId) {
            return this.teamLeadSlots.absences[date]?.[teamLeadId] || [];
        },
        getWeekschedulForDateAndPerson(date, person) {
            if (!person?.id) return null;
            
            let weekSchedule = this.teamLeadSlots.planned_weekschedules[person.id]?.[date] ?? null;
            if (!weekSchedule) {
                weekSchedule = this.teamLeadSlots.default_weekschedules?.[date] ?? null;
            }
            return weekSchedule;
        },

        getBorderColorForDateAndPerson(date, person) {
            if (!person?.id) return {};

            const weekSchedule = this.getWeekschedulForDateAndPerson(date, person);
            if (!weekSchedule || !weekSchedule.is_working) {
                return {
                    borderColor: '#F00',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
            }

            if (this.isPersonOverplanned(date, person)) {
                return {
                    borderColor: '#F00',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
            }

            return {};
        },

        isPersonOverplanned(date, person) {
            if (!person?.id) return false;

            const weekSchedule = this.getWeekschedulForDateAndPerson(date, person);
            const absencesForPerson = this.teamLeadSlots.absences[date]?.[person.id] ?? [];
            let timeToWorkInitially = weekSchedule?.duration ?? 0;
            let timePlanned = 0;

            // Subtract absences
            absencesForPerson.forEach(absence => {
                timeToWorkInitially -= absence.duration;
            });

            // Calculate planned time from all slots for this date
            // Check each slot to see if this person is assigned to it
            Object.values(this.teamLeadSlots.slots).forEach(slot => {
                if (slot.date === date && slot.person_ids.includes(person.id)) {
                    timePlanned += slot.duration;
                }
            });

            // If not working, any planned time is overplanned
            if (!weekSchedule?.is_working) {
                return timePlanned > 0;
            }
            
            return timeToWorkInitially - timePlanned < 0;
        },

        getBorderColorForSlotAndEquipment(slot, equipmentId) {
            if (!this.isEquipmentOverlapping(slot, equipmentId)) return {};

            return {
                borderColor: '#F00',
                borderWidth: '2px',
                borderStyle: 'solid'
            };
        },

        isEquipmentOverlapping(slot, equipmentId) {
            return this.teamLeadSlots.equipment_conflicts?.[equipmentId]?.[slot.date]?.includes(slot.id) ?? false;
        },

        getSlotBorderStyle(slot) {
            let borderStyles = {
                borderColor: '#CCC',    
                borderWidth: '2px',
                borderStyle: 'solid'
            };

            // Check if any person in the slot is overplanned
            const hasOverplannedPerson = slot.person_ids.some(personId => {
                const person = this.teamLeadSlots.persons[personId];
                return person && this.isPersonOverplanned(slot.date, person);
            });

            const hasOverlappingEquipment = slot.equipment_ids.some(equipmentId => this.isEquipmentOverlapping(slot, equipmentId));

            if (hasOverplannedPerson || hasOverlappingEquipment) {
                borderStyles.borderColor = '#F00';
                borderStyles.borderWidth = '2px';
            }

            return borderStyles;
        },
        getOrderedPersonIds(personIds) {
            return [...personIds].sort((a, b) => {
                const personA = this.getPerson(a);
                const personB = this.getPerson(b);
                
                if (!personA || !personB) return 0;
                
                // Sort by job_id first (null values last)
                if (personA.job_id === null && personB.job_id !== null) return 1;
                if (personA.job_id !== null && personB.job_id === null) return -1;
                if (personA.job_id !== personB.job_id) return personA.job_id - personB.job_id;
                
                // If job_id is the same, sort by name
                return personA.name.localeCompare(personB.name);
            });
        },
        getOrderedTeamLeadsForDate(date) {
            const teamLeadSlots = this.getTeamLeadSlotsForDate(date);
            const result = [];
            
            Object.entries(teamLeadSlots).forEach(([teamLeadId, slots]) => {
                result.push({
                    id: teamLeadId,
                    name: this.getTeamLeadName(teamLeadId),
                    slots: slots
                });
            });
            
            // Sort: None (id 0) first, then by person name
            result.sort((a, b) => {
                // Always put '0' first (None)
                if (a.id == '0' || a.id == 0) return -1;
                if (b.id == '0' || b.id == 0) return 1;
                
                // Sort others by name (which we already have)
                return a.name.localeCompare(b.name);
            });
            
            return result;
        },
    },
    watch: {
        dates: function () {
            this.getSlots();
        },
        filter: {
            handler: function () {
                this.getSlots();
            },
            deep: true
        },
        filterLoaded: function () {
            this.getSlots();
        },
    },
    created() {
        this.getSlots();

        // Store reference to the handler so we can remove it specifically
        this.handlePlanningSlots = () => {
            this.getSlots();
        };

        emitter.on('planning-slots-changed', this.handlePlanningSlots);
    },
    beforeUnmount() {
        emitter.off('planning-slots-changed', this.handlePlanningSlots);
        emitter.off('drag-end');
    }
}
</script>

<style scoped lang="scss">
.team-lead-day-planning{
    display: inline-flex;
    height: calc(100vh - 100px);
    min-width: calc(100vw - 340px);
}

.team-lead-day-planning__day {
    display: flex;
}

.team-lead-block {
    display: flex;
    flex-direction: column;
    width: 300px;
    height: 100%;
    border-left: 0px;
    border-right: 1px solid #ccc;
    border-top: 1px solid #ccc;
}

.team-lead-blocks{
    display: flex;
    height: 100%;
}

.team-lead-day__day {
    background: #FFF;
    line-height: 49px;
    text-align: center;
    min-width: 600px;
    border-right: 1px solid #ccc;
    font-weight: bold;
}

.team-lead-name{
    background: #FFF;
    line-height: 40px;
    text-align: center;
    min-width: 100%;
    border-bottom: 2px solid #ccc;
}

.team-lead-slots{
    padding: 5px;
}

.team-lead-slot{
    padding-bottom: 0px;
    border: 1px solid #ccc;
    margin-bottom: 5px;
    background: #FFF;
}

.team-lead-slot-info{
    padding: 5px;
}

.team-lead-slot-title{
    font-weight: bold;
    padding-bottom: 5px;
}

.team-lead-slot-timing-duration{
    font-size: 12px;
    padding: 2px;
    line-height: 10px;
    background: #999;
    color: #FFF;
}

.team-lead-slot-asset{
    background: #999;
    color: #FFF;
    display: inline-block;
    padding: 5px;
    margin: 0 5px 5px 0;
    position: relative;
}

.status-dot {
    display: inline-block;
    width: 8px;
    min-width: 8px;
    max-width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-left: 5px;
    vertical-align: middle;

    &.green {
        background-color: #4CAF50;
    }

    &.red {
        background-color: #f44336;
    }
}
</style>